import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi, saveAuth } from "../api/auth";
import "../styles/auth.css";
import { readNext, stashNext, withNextFrom } from "../lib/nextUrl";
import { phoneError } from "../lib/phone";
import { COUNTRY_CODES } from "../data/countryDialCodes";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const ERROR_MESSAGES = {
  "Email already exists":
    "An account with this email already exists. Try signing in instead.",
  "Network Error":
    "Connection failed. Please check your internet and try again.",
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirm: "",
    // India first: it is where almost every signup comes from, so it is the
    // default rather than a choice everyone has to make.
    countryCode: "+91",
    phoneNumber: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const getFriendlyError = (msg) =>
    ERROR_MESSAGES[msg] || msg || "Something went wrong. Please try again.";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.password) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match. Please check and try again.");
      return;
    }
    // Same rule the resource gate and the server enforce, via lib/phone.js.
    const phoneMsg = phoneError(form.countryCode, form.phoneNumber);
    if (phoneMsg) {
      setError(phoneMsg);
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.register({
        name: form.name,
        email: form.email,
        password: form.password,
        countryCode: form.countryCode,
        phoneNumber: form.phoneNumber,
      });
      saveAuth(res.token, res.user);
      // Same ?next= contract as login, so a registration prompted from a
      // workbook or a checkout returns there rather than to the course list.
      navigate(readNext(location.search), { replace: true });
    } catch (err) {
      setError(getFriendlyError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Google comes back to /login (the backend builds that URL), so the
    // destination is parked for the return leg: see lib/nextUrl.
    stashNext(readNext(location.search));
    window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#f2efe8" }}>
      <Navbar />
      <div className="auth-page">
        <div className="auth-card">
        <Link to="/" className="logo">
          <span className="auth-logo-text">
            ScriptedCAD
          </span>
        </Link>

        <h1 className="auth-heading">Create your account</h1>
        <p className="auth-sub">Start learning CAD automation today</p>

        {/* Google SSO */}
        <button
          className="auth-sso-btn"
          onClick={handleGoogleLogin}
          type="button"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
              fill="#4285F4"
            />
            <path
              d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z"
              fill="#34A853"
            />
            <path
              d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
              fill="#FBBC05"
            />
            <path
              d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <div className="auth-divider">
          <span>or</span>
        </div>

        {error && (
          <div className="auth-error">
            {error}
            {error.includes("already exists") && (
              <Link to={withNextFrom(location.search, "/login")} className="auth-error-link">
                {" "}
                Sign in →
              </Link>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label>Full name</label>
            <input
              type="text"
              value={form.name}
              onChange={update("name")}
              placeholder="Your name"
              autoFocus
            />
          </div>
          <div className="auth-field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
            />
          </div>
          <div className="auth-field">
            <label>WhatsApp number</label>
            <div style={{ display: "flex", gap: 8 }}>
              <select
                value={form.countryCode}
                onChange={update("countryCode")}
                style={{ flex: "0 0 96px" }}
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.id} value={c.dial}>
                    {c.dial} {c.id}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                value={form.phoneNumber}
                onChange={update("phoneNumber")}
                placeholder="9876543210"
                style={{ flex: "1 1 auto", minWidth: 0 }}
              />
            </div>
            <p className="auth-hint">
              We send new CAD automation resources here. Reply STOP any time.
            </p>
          </div>
          <div className="auth-field">
            <label>Password</label>
            <div className="auth-input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={update("password")}
                placeholder="At least 6 characters"
              />
              <button
                type="button"
                className="auth-show-pw"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <div className="auth-field">
            <label>Confirm password</label>
            <div className="auth-input-wrap">
              <input
                type={showConfirm ? "text" : "password"}
                value={form.confirm}
                onChange={update("confirm")}
                placeholder="Same password again"
              />
              <button
                type="button"
                className="auth-show-pw"
                onClick={() => setShowConfirm(!showConfirm)}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          <button type="submit" className="auth-btn" disabled={loading}>
            {loading && <span className="auth-spinner" />}
            {loading ? "Creating account..." : "Create account"}
          </button>
        </form>

        <div className="auth-switch">
          Already have an account?{" "}
          <Link to={withNextFrom(location.search, "/login")}>Sign in →</Link>
        </div>
        <div className="auth-switch" style={{ marginTop: ".5rem" }}>
          <Link to="/">← Back to home</Link>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegisterPage;
